// `supa-base` é usado apenas no cliente para obter sessão; importe dinamicamente
// para evitar inicialização do cliente no servidor.
import { num } from "@/lib/estoque";
import { obterClienteDados } from "@/lib/obter-dados";
import { obterUsuarioCliente } from "@/lib/cliente-dados-helper";

export type Aviso = {
  id: string;
  regra: string;
  entidade: string;
  entidade_id: string;
  titulo: string;
  detalhe: string;
  detectado_em: string;
  resolvido_em: string | null;
};

type Detectado = {
  regra: string;
  entidade: string;
  entidade_id: string;
  titulo: string;
  detalhe: string;
  /** Estoque específico da ocorrência (identificador do estoque cadastrado).
   * Permite contar cada ocorrência separadamente dentro de um mesmo item/regra. */
  local: string;
};

/** Rótulo amigável de cada regra, exibido na aba Avisos */
export const REGRAS: Record<string, string> = {
  minimo_gramas: "Estoque mínimo em gramas com valor muito baixo",
  minimo_ml: "Estoque mínimo em mililitros com valor muito baixo",
  sem_embalagem: "Insumo sem embalagem de compra associada",
  sem_categoria: "Insumo sem categoria associada",
};

/**
 * Regra 1 e 2: insumos cuja unidade base é "g" (ou "ml") com estoque mínimo
 * maior que zero e menor que 1000 — provável confusão com kg (ou litros).
 * O valor é considerado regular quando:
 *   • o insumo é fracionado e o local é a loja; ou
 *   • o estoque mínimo é igual ou superior ao conteúdo de uma embalagem.
 */
async function regrasEstoqueMinimo(): Promise<Detectado[]> {
  const banco = await obterClienteDados();
  const [{ data: insumos, error }, { data: minimos, error: e2 }, { data: estoques, error: e3 }] =
    await Promise.all([
      (banco as any)
        .from("insumos")
        .select("id, nome, sigla, unidade:unidadeTrabalho, fracionado, conteudo_embalagem"),
      (banco as any).from("insumos_minimos").select("ingredient_id, estoque_id, quantidade"),
      (banco as any).from("estoques_depositos").select("id, nome").eq("estoqueAtivo", 1),
    ]);
  if (error) throw error;
  if (e2) throw e2;
  if (e3) throw e3;

  const nomes = new Map((estoques ?? []).map((e) => [e.id, e.nome]));

  const achados: Detectado[] = [];
  for (const ing of insumos ?? []) {
    const unidade = ing.unidade;
    if (unidade !== "g" && unidade !== "ml") continue;
    const maior = unidade === "g" ? "kg" : "litros";
    const embalagem = Number(ing.conteudo_embalagem ?? 0);

    for (const m of minimos ?? []) {
      if (m.ingredient_id !== ing.id) continue;
      if (!nomes.has(m.estoque_id)) continue;
      const valor = Number(m.quantidade ?? 0);
      // valor equivalente a uma embalagem ou mais é considerado regular
      if (embalagem > 0 && valor >= embalagem) continue;
      if (valor > 0 && valor < 1000) {
        achados.push({
          regra: unidade === "g" ? "minimo_gramas" : "minimo_ml",
          entidade: "insumos",
          entidade_id: ing.id,
          titulo: `${ing.nome}${ing.sigla ? ` (${ing.sigla})` : ""}`,
          local: m.estoque_id,
          detalhe:
            `Estoque mínimo em ${nomes.get(m.estoque_id)} abaixo de 1000 ${unidade}: ${num(valor, 3)} ${unidade}. ` +
            `Confira se o valor não foi digitado em ${maior}.`,
        });
      }
    }
  }
  return achados;
}

/**
 * Regras 3 e 4: insumos sem embalagem de compra vinculada (tabela de
 * embalagens) e insumos sem categoria.
 */
async function regrasCadastroIncompleto(): Promise<Detectado[]> {
  const banco = await obterClienteDados();
  const { data, error } = await (banco as any)
    .from("insumos")
    .select("id, nome, sigla, conteudo_embalagem, categoria_id");
  if (error) throw error;

  const achados: Detectado[] = [];
  for (const ing of data ?? []) {
    const titulo = `${ing.nome}${ing.sigla ? ` (${ing.sigla})` : ""}`;
    if (!Number(ing.conteudo_embalagem)) {
      achados.push({
        regra: "sem_embalagem",
        entidade: "insumos",
        entidade_id: ing.id,
        titulo,
        local: "",
        detalhe:
          "Insumo sem conteúdo de embalagem informado. Enquanto isso, o sistema considera " +
          "o conteúdo 1.",
      });
    }
    if (!ing.categoria_id) {
      achados.push({
        regra: "sem_categoria",
        entidade: "insumos",
        entidade_id: ing.id,
        titulo,
        local: "",
        detalhe: "Insumo sem categoria associada. Escolha uma categoria no cadastro do insumo.",
      });
    }
  }
  return achados;
}

/** Todas as regras ativas. Novas regras podem ser acrescentadas nesta lista. */
const VERIFICACOES: (() => Promise<Detectado[]>)[] = [
  // regrasEstoqueMinimo, // suspensa: avisos de estoques mínimos suspeitos desativados
  regrasCadastroIncompleto,
];

async function ehAdmin() {
  if (typeof window === "undefined") return false;
  const usuario = await obterUsuarioCliente();
  if (!usuario) return false;
  const banco = await obterClienteDados();
  const { data: nivel } = await (banco as any).rpc("role_level", { _user_id: usuario.id });
  return Number(nivel ?? 9) === 1;
}

/**
 * Executa todas as regras e sincroniza a tabela de avisos:
 * novos são inseridos, os que deixaram de existir ficam marcados como
 * resolvidos (histórico) e os antigos com mais de 30 dias são apagados.
 * Só roda para administradores.
 */
export async function verificarInconsistencias(): Promise<number> {
  if (!(await ehAdmin())) return 0;

  const listas = await Promise.all(VERIFICACOES.map((fn) => fn()));
  const achados = listas.flat();
  const chave = (a: {
    regra: string;
    entidade: string;
    entidade_id: string;
    local: string | null;
  }) => `${a.regra}|${a.entidade}|${a.entidade_id}|${a.local ?? ""}`;
  const atuais = new Set(achados.map(chave));
  const banco = await obterClienteDados();
  const { data: existentes, error } = await banco
    .from("avisos_inconsistencia")
    .select("id, regra, entidade, entidade_id, local, resolvido_em");
  if (error) throw error;

  if (achados.length) {
    const { error: e1 } = await banco.from("avisos_inconsistencia").upsert(
      achados.map((a) => ({ ...a, resolvido_em: null })),
      { onConflict: "regra,entidade,entidade_id,local" },
    );
    if (e1) throw e1;
  }

  const resolver = (existentes ?? [])
    .filter((e) => !e.resolvido_em && !atuais.has(chave(e)))
    .map((e) => e.id);
  if (resolver.length) {
    await banco
      .from("avisos_inconsistencia")
      .update({ resolvido_em: new Date().toISOString() })
      .in("id", resolver);
  }

  const limite = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  await banco
    .from("avisos_inconsistencia")
    .delete()
    .not("resolvido_em", "is", null)
    .lt("resolvido_em", limite);

  return achados.length;
}
